import AddIcon from "@mui/icons-material/Add";
import { Button, CircularProgress, Stack } from "@mui/material";
import { type MutationKey, useMutationState } from "@tanstack/react-query";
import { useAdminRaffleRoundsQuery } from "Api/Queries/Raffle/useAdminRaffleRoundsQuery";
import {
  RaffleExportIntegrityError,
  useDownloadRaffleExportMutation,
} from "Api/Mutations/Raffle/useDownloadRaffleExportMutation";
import {
  type RaffleRoundLifecycleAction,
  raffleRoundLifecycleKeys,
  useAnnounceRaffleRoundMutation,
  useCancelRaffleRoundMutation,
  useCloseRaffleRoundMutation,
  useCompleteRaffleRoundMutation,
  useSetDefaultRaffleRoundMutation,
} from "Api/Mutations/Raffle/useRaffleRoundLifecycleMutations";
import { RaffleHolderLookupDialog } from "Components/Admin/Raffle/RaffleHolderLookupDialog";
import { RaffleRoundViewDialog } from "Components/Admin/Raffle/RaffleRoundViewDialog";
import { RaffleRoundDialog } from "Components/Admin/Raffle/RaffleRoundDialog";
import { RaffleRoundsTable } from "Components/Admin/Raffle/RaffleRoundsTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useCheckPermission } from "Hooks/usePermission";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { AccessRightCodes } from "Models/AccessRightCodes";
import {
  Permission,
  type RaffleRoundDto,
  RaffleRoundStatus,
} from "Infrastructure/Api/Api";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.Raffle;

type PendingAction = {
  kind: RaffleRoundLifecycleAction;
  round: RaffleRoundDto;
};

type RoundAction = {
  title: string;
  content: string;
  isDestructive: boolean;
  run: (publicID: string) => void;
};

/** Closing flips only isOpen, so a dialog keeps its content while it fades out. */
type RoundDialogState = {
  isOpen: boolean;
  round: RaffleRoundDto | null;
};

const CLOSED_DIALOG: RoundDialogState = { isOpen: false, round: null };

const closeDialog = (state: RoundDialogState): RoundDialogState => ({
  ...state,
  isOpen: false,
});

const isString = (value: unknown): value is string => typeof value === "string";

const OPEN_STATUSES: ReadonlyArray<RaffleRoundStatus> = [
  RaffleRoundStatus.Draft,
  RaffleRoundStatus.Announced,
];

// Read from the mutation cache, so every in-flight row is tracked, not just each hook's last call.
const usePendingLifecycleRoundIDs = (mutationKey: MutationKey) =>
  useMutationState({
    filters: { mutationKey, status: "pending" },
    select: (mutation) => mutation.state.variables,
  }).filter(isString);

export const RaffleRoundsPage = () => {
  const { t } = useResource();
  const [formDialog, setFormDialog] = useState(CLOSED_DIALOG);
  const [holderLookupDialog, setHolderLookupDialog] = useState(CLOSED_DIALOG);
  const [viewDialog, setViewDialog] = useState(CLOSED_DIALOG);
  // Never cleared, for the same reason as RoundDialogState.
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );
  const actionConfirm = useConfirmDialog();

  const { data: rounds, isError, isPending } = useAdminRaffleRoundsQuery();

  // Same rule as the BE: a market without an open main round gets the new round as its main round.
  const regionsWithOpenDefault = (rounds ?? [])
    .filter((round) => round.isDefault && OPEN_STATUSES.includes(round.status))
    .map((round) => round.region);

  // Each affordance mirrors its endpoint's permission (AdminLite holds only Read|Export).
  const canCreate = useCheckPermission(
    AccessRightCodes.Raffle,
    Permission.Create,
  );
  const canManage = useCheckPermission(
    AccessRightCodes.Raffle,
    Permission.Update,
  );
  const canExport = useCheckPermission(
    AccessRightCodes.Raffle,
    Permission.Export,
  );

  const { mutate: announceRound } = useAnnounceRaffleRoundMutation({
    onSuccess: () => toast.success(t(RESOURCES.Messages.AnnounceSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.AnnounceError)),
  });
  const { mutate: closeRound } = useCloseRaffleRoundMutation({
    onSuccess: () => toast.success(t(RESOURCES.Messages.CloseSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.CloseError)),
  });
  const { mutate: completeRound } = useCompleteRaffleRoundMutation({
    onSuccess: () => toast.success(t(RESOURCES.Messages.CompleteSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.CompleteError)),
  });
  const { mutate: cancelRound } = useCancelRaffleRoundMutation({
    onSuccess: () => toast.success(t(RESOURCES.Messages.CancelSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.CancelError)),
  });
  const { mutate: setDefaultRound } = useSetDefaultRaffleRoundMutation({
    onSuccess: () => toast.success(t(RESOURCES.Messages.SetDefaultSuccess)),
    onError: () => toast.error(t(RESOURCES.Messages.SetDefaultError)),
  });
  const { mutate: downloadExport, isPending: isExporting } =
    useDownloadRaffleExportMutation();

  // A round's actions stay locked while one of its requests runs, so Close cannot fire twice.
  const busyRoundIDs = usePendingLifecycleRoundIDs(
    raffleRoundLifecycleKeys.all,
  );
  const closingRoundIDs = usePendingLifecycleRoundIDs(
    raffleRoundLifecycleKeys.action("close"),
  );

  const getRoundActions = (
    round: RaffleRoundDto,
  ): Record<RaffleRoundLifecycleAction, RoundAction> => {
    const roundParams = { name: round.name, region: round.region };

    return {
      announce: {
        title: t(RESOURCES.Messages.AnnounceConfirmTitle, roundParams),
        content: t(RESOURCES.Messages.AnnounceConfirm),
        isDestructive: false,
        run: announceRound,
      },
      close: {
        title: t(RESOURCES.Messages.CloseConfirmTitle, roundParams),
        // Only the main round opens a successor; an extra competition just ends.
        content: t(
          round.isDefault
            ? RESOURCES.Messages.CloseConfirm
            : RESOURCES.Messages.CloseCompetitionConfirm,
        ),
        isDestructive: true,
        run: closeRound,
      },
      complete: {
        title: t(RESOURCES.Messages.CompleteConfirmTitle, roundParams),
        content: t(RESOURCES.Messages.CompleteConfirm),
        isDestructive: false,
        run: completeRound,
      },
      cancel: {
        title: t(RESOURCES.Messages.CancelConfirmTitle, roundParams),
        content: t(RESOURCES.Messages.CancelConfirm),
        isDestructive: true,
        run: cancelRound,
      },
      setDefault: {
        title: t(RESOURCES.Messages.SetDefaultConfirmTitle, roundParams),
        content: t(RESOURCES.Messages.SetDefaultConfirm),
        isDestructive: false,
        run: setDefaultRound,
      },
    };
  };

  const requestAction =
    (kind: RaffleRoundLifecycleAction) => (round: RaffleRoundDto) => {
      setPendingAction({ kind, round });
      actionConfirm.open();
    };

  const pendingConfirmation = pendingAction && {
    ...getRoundActions(pendingAction.round)[pendingAction.kind],
    publicID: pendingAction.round.publicID,
  };

  return (
    <AuthenticatedLayout title={t(RESOURCES.PageTitle)}>
      {/* Full width: eight columns and the actions do not fit the 600px default. */}
      <StyledPageWrapper $maxWidth="unset">
        <Stack gap={2}>
          {canCreate && (
            <Stack direction="row" justifyContent="flex-end">
              {/* Waits for the rounds, which tell whether the new round becomes a main round. */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={rounds === undefined}
                onClick={() => setFormDialog({ isOpen: true, round: null })}
              >
                {t(RESOURCES.CreateButton)}
              </Button>
            </Stack>
          )}

          {isError && <ErrorText>{t(RESOURCES.Messages.LoadError)}</ErrorText>}
          {isPending && (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          )}

          {!isPending && !isError && (
            <RaffleRoundsTable
              rounds={rounds ?? []}
              canManage={canManage}
              canExport={canExport}
              busyRoundIDs={busyRoundIDs}
              closingRoundIDs={closingRoundIDs}
              isExporting={isExporting}
              emptyText={t(RESOURCES.Table.Empty)}
              onView={(round) => setViewDialog({ isOpen: true, round })}
              onEdit={(round) => setFormDialog({ isOpen: true, round })}
              onAnnounce={requestAction("announce")}
              onClose={requestAction("close")}
              onComplete={requestAction("complete")}
              onCancel={requestAction("cancel")}
              onSetDefault={requestAction("setDefault")}
              onExport={(round) =>
                downloadExport(round, {
                  // Not a hiccup to retry: the draw must not run on a sheet that failed its check.
                  onError: (error) =>
                    toast.error(
                      t(
                        error instanceof RaffleExportIntegrityError
                          ? RESOURCES.Messages.ExportIntegrityError
                          : RESOURCES.Messages.ExportError,
                      ),
                    ),
                })
              }
              onFindHolder={(round) =>
                setHolderLookupDialog({ isOpen: true, round })
              }
            />
          )}
        </Stack>
      </StyledPageWrapper>

      <RaffleRoundDialog
        isOpen={formDialog.isOpen}
        round={formDialog.round}
        regionsWithOpenDefault={regionsWithOpenDefault}
        onClose={() => setFormDialog(closeDialog)}
      />

      <RaffleHolderLookupDialog
        isOpen={holderLookupDialog.isOpen}
        round={holderLookupDialog.round}
        onClose={() => setHolderLookupDialog(closeDialog)}
      />

      <RaffleRoundViewDialog
        isOpen={viewDialog.isOpen}
        round={viewDialog.round}
        onClose={() => setViewDialog(closeDialog)}
      />

      {pendingConfirmation && (
        <ConfirmDialog
          isOpen={actionConfirm.isOpen}
          modalTitle={pendingConfirmation.title}
          modalContent={pendingConfirmation.content}
          isDestructive={pendingConfirmation.isDestructive}
          handleResult={actionConfirm.handleResult(() =>
            pendingConfirmation.run(pendingConfirmation.publicID),
          )}
        />
      )}
    </AuthenticatedLayout>
  );
};
